import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FiSearch, FiMenu, FiSun, FiMoon, FiX } from "react-icons/fi";
import { motion } from "framer-motion";
import img from "../assets/thebox-logo.png";
import { useUI, useAppDispatch } from "../store/hooks";
import { setTheme } from "../store/slices/uiSlice";
import { setCountrySearchQuery } from "../store/slices/searchSlice";

const HeaderContainer = styled.header`
  background: ${(props) => props.theme.colors.surface};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2rem;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;

  span {
    background: linear-gradient(
      135deg,
      ${(props) => props.theme.colors.primary},
      ${(props) => props.theme.colors.secondary}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 500px;
  position: relative;
  margin-left: auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  background: ${(props) => props.theme.colors.card};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.medium};
  color: ${(props) => props.theme.colors.text};
  font-size: 1rem;
  transition: ${(props) => props.theme.transitions.fast};

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: ${(props) => props.theme.shadows.glow};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.textSecondary};
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 1.2rem;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 1.2rem;
  cursor: pointer;
  transition: ${(props) => props.theme.transitions.fast};
  &:hover {
    color: ${(props) => props.theme.colors.text};
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.text};
  font-size: 1.5rem;
  cursor: pointer;
  display: block;
  &:focus {
    outline: none;
  }
`;

const ThemeToggleButton = styled(motion.button)`
  background: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.text};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.round};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: ${(props) => props.theme.transitions.fast};
  
  &:hover {
    color: ${(props) => props.theme.colors.primary};
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const Header = ({ onMenuToggle, sidebarOpen }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { theme } = useUI();

  // Handle live updates for country search
  const handleCountrySearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    dispatch(setCountrySearchQuery(query));
  };

  // Handle submit for channel search
  const handleChannelSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    // Dispatch an empty string to clear the search filter in Redux
    dispatch(setCountrySearchQuery(''));
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const handleLogoClick = () => {
    navigate("/");
    // Clear the search when navigating to the home page
    setSearchQuery('');
    dispatch(setCountrySearchQuery(''));
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    dispatch(setTheme(newTheme));
  };

  // Determine the correct onChange handler based on the current path
  const handleInputChange = (e) => {
    if (location.pathname === '/') {
      handleCountrySearchChange(e);
    } else {
      setSearchQuery(e.target.value);
    }
  };

  // Determine the correct form onSubmit handler
  const handleFormSubmit = (e) => {
    if (location.pathname !== '/') {
      handleChannelSearchSubmit(e);
    } else {
      e.preventDefault(); // Prevent page reload on enter key for country search
    }
  };

  return (
    <HeaderContainer>
      <MenuButton onClick={onMenuToggle}>
        <FiMenu />
      </MenuButton>
      <Logo onClick={handleLogoClick}>
        <img
          src={img}
          style={{ height: "90px", width: "100px", borderRadius: "50%" }}
          alt={"The Box"}
        ></img>
      </Logo>

      <SearchContainer>
        <form onSubmit={handleFormSubmit}>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder={location.pathname === '/' ? "Search countries..." : "Search channels..."}
            value={searchQuery}
            onChange={handleInputChange}
            ref={searchInputRef}
          />
          {searchQuery && (
            <ClearSearchButton type="button" onClick={handleClearSearch}>
              <FiX />
            </ClearSearchButton>
          )}
        </form>
      </SearchContainer>

      <ThemeToggleButton
        onClick={handleThemeToggle}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {theme === 'light' ? <FiMoon /> : <FiSun />}
      </ThemeToggleButton>
    </HeaderContainer>
  );
};

export default Header;